/**
 * FindPasswordContainer
 * --
 */
import React, { useState, useRef } from 'react';
import FindPasswordPresenter from './FindPasswordPresenter';
import { useNavigate } from 'react-router-dom';
import { useSendEmail } from 'hooks/SendHooks';
import MessageAlert from 'utils';
import { LoadingPage } from 'components';
import { useFindAccount } from 'hooks/AuthHooks';
import { useBizSendSMS } from 'hooks/BizHooks';

const FindPasswordContainer = (props) => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();

  /* ===== PROPS ===== */
  const { screenSize } = props;

  /* ===== MUTATE ===== */
  // 이메일 인증
  const { sendEmail } = useSendEmail();
  // 문자 인증
  const { sendSMS } = useBizSendSMS();
  // 아이디 찾기
  const { findAccount } = useFindAccount();

  /* ===== STATE ===== */
  const nameRef = useRef(null);
  const accountRef = useRef(null);
  const authRef = useRef(null);
  const authCodeRef = useRef(null);
  const timerStart = useRef(null);
  const [inputValue, setInputValue] = useState({
    name: '',
    account: '', // 이름
    auth: '', // 인증 수단
    authCode: '', // 입력 인증 번호
  });
  // 탭
  const [tabKey, setTabKey] = useState('phone');
  // Input 상태값
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('default');
  // 인증번호
  const [authCode, setAuthCode] = useState('');
  const [timeText, setTimeText] = useState('');
  // 로딩
  const [isLoading, setIsLoading] = useState(false);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 인증번호 전송
   * --
   */
  const handleSendAuthCode = async () => {
    const { name, account, auth } = inputValue;

    if (!name) {
      setMessage('이름을 입력해주세요!');
      setStatus('error');
      nameRef.current.focus();
      return;
    }

    if (!account) {
      setMessage('아이디를 입력해주세요!');
      setStatus('error');
      accountRef.current.focus();
      return;
    }

    if (!auth) {
      setMessage(
        `${tabKey === 'phone' ? '휴대폰번호를' : '이메일을'} 입력해주세요!`
      );
      setStatus('error');
      authRef.current.focus();
      return;
    }

    // 휴대폰 유효성 검사
    const phoneRegex = /^(010|011|016|017|018|019)-?\d{3,4}-?\d{4}$/;
    if (tabKey === 'phone' && !phoneRegex.test(auth)) {
      setMessage(`잘못된 휴대폰 번호 형식입니다.`);
      setStatus('error');
      authRef.current.focus();
      return;
    }

    // 이메일 유효성 검사
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (tabKey === 'email' && !emailRegex.test(auth)) {
      setMessage(`잘못된 이메일 주소 형식입니다.`);
      setStatus('error');
      authRef.current.focus();
      return;
    }

    try {
      setIsLoading(true);

      // 인증번호 전송
      const bodyData =
        tabKey === 'phone'
          ? {
              to_phone: inputValue?.auth?.replace(/-/g, ''),
              type: 'sms',
              user_type: 'USER',
              is_template: 'Y',
              send_type: 'CODE',
            }
          : {
              to_email: inputValue?.auth,
              type: 'CODE',
              user_type: 'USER',
              is_template: 'Y',
            };

      const { status, data } =
        tabKey === 'phone'
          ? await sendSMS(bodyData)
          : await sendEmail(bodyData);

      if (status !== 200) {
        MessageAlert.warning('인증번호 전송에 실패하였습니다.');
        return;
      }

      setAuthCode(data.code);
      handleStartTimer();
    } catch (err) {
      console.log(`인증번호 전송 실패`, err);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = null) => {
    let newData = { ...inputValue };

    newData[t] = v;
    const d = {
      ...newData,
    };
    setInputValue(d);

    if (callback) callback(v);
  };

  /**
   * 인증코드 입력 시간 제한 설정
   * --
   */
  const handleStartTimer = () => {
    if (timerStart.current) {
      clearInterval(timerStart.current);
      timerStart.current = null;
    }

    let time = 300;
    timerStart.current = setInterval(() => {
      --time;

      if (time === 0) {
        setAuthCode('만료');
        clearInterval(timerStart.current);
        timerStart.current = null;
        setTimeText('시간만료');
        return;
      }

      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(seconds).padStart(2, '0');
      setTimeText(`${formattedMinutes}:${formattedSeconds}`);
    }, 1000);
  };

  /**
   * 인증코드 체크
   * --
   */
  const handleAuthCheck = async () => {
    if (authCode === '만료') {
      setMessage('인증코드가 만료되었습니다. 다시 인증해주세요!');
      setStatus('error');
      return;
    }

    if (!inputValue.authCode || authCode !== inputValue.authCode) {
      setMessage('인증번호가 올바르지 않습니다.');
      setStatus('error');
      authCodeRef.current.focus();
      return;
    }

    try {
      const res = await findAccount({
        user_name: inputValue?.name,
        user_account: inputValue?.account,
        ...(tabKey === 'phone'
          ? { user_phone: inputValue?.auth }
          : { user_email: inputValue?.auth }),
        type: 'USER',
        auth_type: 'PASSWORD',
      });

      if (res.status !== 200) {
        MessageAlert.warning(
          '가입된 회원을 찾을 수 없습니다. 입력하신 정보를 다시 확인해주세요.'
        );
        setAuthCode('');
        setTimeText('');
        return;
      }

      // React Native WebView 인지 체크
      const isNative = window.ReactNativeWebView;
      // Redirect 경로 설정
      const redirectUrl = isNative
        ? `/components/find/password/result`
        : `/find/password/result`;

      navigate(redirectUrl, {
        state: {
          findValue: res,
        },
      });
    } catch (err) {
      console.error('ERROR !! ', err);
    }
  };

  /**
   * 탭키 설정
   * --
   */
  const handleTabKey = (key) => {
    if (authCode) {
      const confirm = window.confirm(
        '인증 진행중입니다. 인증 수단을 변경하여 다시 시도하시겠습니까?'
      );

      if (confirm) {
        setInputValue({
          name: '',
          account: '',
          auth: '',
          authCode: '',
        });
        setAuthCode('');
        setTimeText('');
        setTabKey(key);
      } else return;
    } else {
      setTabKey(key);
    }
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  return (
    <>
      <FindPasswordPresenter
        nameRef={nameRef}
        accountRef={accountRef}
        authRef={authRef}
        authCodeRef={authCodeRef}
        inputValue={inputValue}
        tabKey={tabKey}
        message={message}
        status={status}
        timeText={timeText}
        authCode={authCode}
        onTabChange={handleTabKey}
        onChange={handleChange}
        onSend={handleSendAuthCode}
        onSubmit={handleAuthCheck}
        screenSize={screenSize}
      />
      {isLoading ? (
        <LoadingPage text={'인증코드를 전송중입니다. 잠시만 기다려주세요.'} />
      ) : null}
    </>
  );
};

export default FindPasswordContainer;
